import React, { useState, useEffect, useRef, useCallback } from "react";
import SaveButtonWithModal from "./SaveButtonWithModel";
import { uploadToCloudinary } from "../utils/CloudinaryUpload";

const LOCAL_STORAGE_KEY = "organizationFormData";

const OrganizationForm = ({
  formData,
  onFormDataChange,
  selectedOption,
  onCertificatesUrlsChange,
  onIdUrlChange,
  onOptionChange,
  onSave,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedCircles, setSelectedCircles] = useState({});
  const [images, setImages] = useState(Array(4).fill(null));
  const [certFiles, setCertFiles] = useState(Array(4).fill(null));
  const [showModal, setShowModal] = useState(false);
  const [idFile, setIdFile] = useState(null);
  const [idImageUrl, setIdImageUrl] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [documentDropdownOpen, setDocumentDropdownOpen] = useState(false);
  const [selectedDocumentType, setSelectedDocumentType] = useState("Documents / Certificates");
  const [boxPlaceholders, setBoxPlaceholders] = useState([
    "National identity card",
    "Business registration",
    "Trade Certificate",
    "Tax compliance",
    "National identity card",
    "Business registration",
    "Trade Certificate",
    "Tax compliance",
    "National identity card",
    "Business registration",
    "Trade Certificate",
    "Tax compliance",
  ]);
  const [currentBoxIndex, setCurrentBoxIndex] = useState(0);

  // Add this function to handle saving to localStorage
  const handleSaveToLocalStorage = useCallback(() => {
    const dataToSave = {
      images,
      idImageUrl,
      boxPlaceholders,
      formData
    };
    localStorage.setItem('organizationSectionData', JSON.stringify(dataToSave));
    setError("Organization data saved locally!");
    setTimeout(() => setError(""), 3000);
  }, [images, idImageUrl, boxPlaceholders, formData]);

  // Scroll functionality
  const contentRef = useRef(null);
  const [scrollTop, setScrollTop] = useState(0);
  const [thumbHeight, setThumbHeight] = useState(40);
  const [isDragging, setIsDragging] = useState(false);
  const [startY, setStartY] = useState(0);
  const [startScrollTop, setStartScrollTop] = useState(0);
  const thumbRef = useRef(null);

  // Calculate thumb height based on content height
  useEffect(() => {
    const content = contentRef.current;
    if (!content) return;

    const updateThumbHeight = () => {
      const { scrollHeight, clientHeight } = content;
      const visibleRatio = clientHeight / scrollHeight;
      setThumbHeight(Math.max(visibleRatio * clientHeight, 20));
    };

    updateThumbHeight();
    window.addEventListener("resize", updateThumbHeight);
    return () => window.removeEventListener("resize", updateThumbHeight);
  }, [documentDropdownOpen]);

  // Handle scroll events
  const handleScroll = () => {
    const content = contentRef.current;
    if (!content) return;

    const { scrollTop, scrollHeight, clientHeight } = content;
    const maxScrollTop = scrollHeight - clientHeight;
    const scrollRatio = scrollTop / maxScrollTop;
    const trackHeight = content.clientHeight - thumbHeight;
    setScrollTop(scrollRatio * trackHeight);
  };

  // Mouse down on thumb
  const handleThumbMouseDown = (e) => {
    e.preventDefault();
    e.stopPropagation();

    setIsDragging(true);
    setStartY(e.clientY);
    setStartScrollTop(contentRef.current.scrollTop);

    document.addEventListener("mousemove", handleThumbMouseMove);
    document.addEventListener("mouseup", handleThumbMouseUp);
    document.body.style.cursor = "grabbing";
  };

  // Mouse move when dragging thumb
  const handleThumbMouseMove = (e) => {
    if (!isDragging) return;

    const deltaY = e.clientY - startY;
    const content = contentRef.current;
    const { scrollHeight, clientHeight } = content;
    const maxScrollTop = scrollHeight - clientHeight;
    const trackHeight = clientHeight - thumbHeight;

    const scrollDelta = (deltaY / trackHeight) * maxScrollTop;
    const newScrollTop = Math.min(
      Math.max(0, startScrollTop + scrollDelta),
      maxScrollTop
    );

    content.scrollTop = newScrollTop;
  };

  // Mouse up when dragging thumb
  const handleThumbMouseUp = () => {
    setIsDragging(false);
    document.removeEventListener("mousemove", handleThumbMouseMove);
    document.removeEventListener("mouseup", handleThumbMouseUp);
    document.body.style.cursor = "";
  };

  // Click on track to jump to position
  const handleTrackClick = (e) => {
    const content = contentRef.current;
    if (!content || e.target === thumbRef.current) return;

    const track = e.currentTarget;
    const trackRect = track.getBoundingClientRect();
    const clickPosition = e.clientY - trackRect.top;
    const thumbCenter = thumbHeight / 2;
    const scrollRatio =
      (clickPosition - thumbCenter) / (trackRect.height - thumbHeight);

    const { scrollHeight, clientHeight } = content;
    const maxScrollTop = scrollHeight - clientHeight;
    content.scrollTop = scrollRatio * maxScrollTop;
  };

  // Restore state from localStorage on mount
  useEffect(() => {
    const data = localStorage.getItem(LOCAL_STORAGE_KEY);
    if (data) {
      const parsed = JSON.parse(data);
      setImages(parsed.images || Array(4).fill(null));
      setBoxPlaceholders(parsed.boxPlaceholders || [
        "National identity card",
        "Business registration",
        "Trade Certificate",
        "Tax compliance",
        "National identity card",
        "Business registration",
        "Trade Certificate",
        "Tax compliance",
        "National identity card",
        "Business registration",
        "Trade Certificate",
        "Tax compliance",
      ]);
      setCurrentBoxIndex(parsed.currentBoxIndex || 0);
      setSelectedDocumentType(parsed.selectedDocumentType || "Documents / Certificates");
    }
  }, []);

  const documentTypes = [
    "Awards",
    "Account Resolution",
    "Bank Account",
    "B-BBEE- Affidavit/Certificate",
    "Business Insurance",
    "Business Plan & Financial Records",
    "Business registration",
    "Certified ID",
    "Company profile",
    "COOP /PTY LTD",
    "Drivers License",
    "Environmental Impact assessment (EIA)",
    "GAP certificate",
    "Health Certificate",
    "Industry-Specific",
    "Licenses or Permits",
    "Letter",
    "Membership of organization",
    "National Identity",
    "Trade License",
    "Title deed",
    "Proof of residence",
    "Prof of education",
    "Site Lease /PTO.",
    "Ownership docs",
    "Reg documents",
    "Tax Clearance",
    "Training Certificate",
    "Proof of bank",
    "Passport",
    "Others"
  ];

  const toggleCircle = (item) => {
    setSelectedCircles((prev) => ({
      ...prev,
      [item]: !prev[item],
    }));
  };

  const handleOptionClick = (option) => {
    onOptionChange(option);
    setIsOpen(false);
  };

  const [isSelected, setSelected] = useState();
  const handleChange = (e) => {
    const { name, value } = e.target;
    onFormDataChange({ ...formData, [name]: value });
  };

  const handleDeleteImage = (index) => {
    const newImages = [...images];
    newImages[index] = null;
    setImages(newImages);
    
    const newFiles = [...certFiles];
    newFiles[index] = null;
    setCertFiles(newFiles);
    
    // Update parent with filtered non-null images
   
  };
  const handleImageChange = (e, index) => {
    const file = e.target.files[0];
    if (!file) return;
  
    const imageData = {
      previewUrl: URL.createObjectURL(file),
      url: URL.createObjectURL(file),
      file,
      name: file.name,
      type: file.type,
      size: file.size,
      documentType: boxPlaceholders[index], // Track document type
      cloudinaryUrl: null
    };
  
    const newImages = [...images];
    newImages[index] = imageData;
    setImages(newImages);
  
    const newFiles = [...certFiles];
    newFiles[index] = file;
    setCertFiles(newFiles);
  
    if (onCertificatesUrlsChange) {
      onCertificatesUrlsChange(
        newImages.filter(img => img !== null).map(img => ({
          url: img.previewUrl,
          type: img.documentType, // Include document type
          documentType: img.documentType, // Add documentType field
          file: img.file
        }))
      );
    }
  };

  const handleIdChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setIdFile(file);
      setIdImageUrl(URL.createObjectURL(file));
    }
  };

  const handleDocumentTypeSelect = (type) => {
    setSelectedDocumentType(type);
    setDocumentDropdownOpen(false);
    
    const newPlaceholders = [...boxPlaceholders];
    newPlaceholders[currentBoxIndex] = type;
    setBoxPlaceholders(newPlaceholders);
    
    setCurrentBoxIndex(prevIndex => (prevIndex + 1) % 12);
  };

  const handleSave = async () => {
    const hasCertificates = images.some(img => img !== null);
    setSelectedDocumentType("Documents / Certificates");
    
    if (!hasCertificates) {
      setError("At least one certificate is required.");
      setShowModal(false);
      return;
    }
  
    setError("");
    setSaving(true);
    
    try {
      const uploadPromises = images
      .filter(img => img !== null)
      .map(async (img, index) => {
        // Return existing Cloudinary URL if available
        if (img.cloudinaryUrl) {
          return {
            url: img.url || img.previewUrl,
            previewUrl: img.previewUrl,
            cloudinaryUrl: img.cloudinaryUrl,
            type: boxPlaceholders[index],
            documentType: boxPlaceholders[index],
            file: img.file,
            storagePath: img.storagePath
          };
        }
        
        // Upload new files
        if (img.file instanceof Blob) {
          const uploadResult = await uploadToCloudinary(img.file, {
            folder: 'certificates',
            public_id: `${formData.registrationNumber || 'temp'}_${Date.now()}_${index}`
          });
          
          return {
            url: uploadResult.secure_url,
            previewUrl: img.previewUrl,
            cloudinaryUrl: uploadResult.secure_url,
            type: boxPlaceholders[index],
            documentType: boxPlaceholders[index],
            file: img.file,
            storagePath: `certificates/${formData.registrationNumber}_${index}`
          };
        }
        
        return {
          url: img.url || img.previewUrl,
          previewUrl: img.previewUrl,
          cloudinaryUrl: img.cloudinaryUrl,
          type: boxPlaceholders[index],
          documentType: boxPlaceholders[index],
          file: img.file,
          storagePath: img.storagePath
        };
      });

    const certUrls = await Promise.all(uploadPromises);
    
    // Update parent component with full image data
    if (onCertificatesUrlsChange) {
      onCertificatesUrlsChange(certUrls);
    }

      setSaving(false);
      setShowModal(true);
  
      onSave({
        ...formData,
        certificates: certUrls,
        idDocument: idImageUrl,
        typeOfOrganization: selectedOption || "COOPERATIVE 1",
      });
    } catch (error) {
      console.error("Upload error:", error);
      setError("Failed to upload files. Please try again.");
      setSaving(false);
    }
  };

  useEffect(() => {
    return () => {
      // Clean up object URLs
      images.forEach(img => {
        if (img && img.previewUrl) {
          URL.revokeObjectURL(img.previewUrl);
        }
      });
      if (idImageUrl) URL.revokeObjectURL(idImageUrl);
    };
  }, [images, idImageUrl]);

  return (
    <div className="md:px-0 md:mt-6">
      <div className="">
        <div className="mb-6">
          <div className="flex items-center gap-2">
            <div 
              className="relative cursor-pointer"
              onClick={() => setDocumentDropdownOpen(!documentDropdownOpen)}
            >
              <div className="flex items-center md:gap-32 gap-1">
                <span className="text-[8px] md:text-[36px] font-bold">
                  {selectedDocumentType}
                </span>
                <svg
                  width={window.innerWidth > 768 ? "24" : "12"}
                  height={window.innerWidth > 768 ? "24" : "12"}
                  viewBox="0 0 24 24"
                  fill="none"
                  className={`text-black transition-transform ${
                    documentDropdownOpen ? "rotate-180" : ""
                  }`}
                >
                  <path
                    d="M6 9L12 15L18 9"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
              
              {documentDropdownOpen && (
                <div className="absolute top-full left-0 mt-2 md:mt-1 z-10 bg-white border border-gray-200 rounded-md shadow-lg py-2  w-[300px] md:w-[300px] max-h-[400px] flex">
                  <div className="flex-1 flex flex-col">
                    <div className="px-3 md:px-2 md:pb-2 flex justify-between">
                      <h1 className="text-[8px] md:text-[20px] font-bold text-black">
                      Document
                      </h1>
                      <img
                        src="https://res.cloudinary.com/dzngpgki4/image/upload/v1750034880/Export_1_vurzim.png"
                        alt=""
                        className="md:w-[43px] h-4 w-4 md:h-[42px]"
                        onClick={(e) => {
                          e.stopPropagation();
                          setDocumentDropdownOpen(false);
                        }}
                      />
                    </div>

                    <div className="w-full h-5 flex justify-end">
                      <img
                        src="https://res.cloudinary.com/dzngpgki4/image/upload/v1747461105/Expand_up_mflvtu.png"
                        alt="scroll-up"
                        className="md:w-4 md:h-4 h-2 w-2 cursor-pointer"
                        onClick={() => {
                          contentRef.current.scrollBy({
                            top: -50,
                            behavior: "smooth",
                          });
                        }}
                      />
                    </div>

                    <div className="custom-scroll-area">
                      <div
                        className="custom-scroll-content"
                        ref={contentRef}
                        onScroll={handleScroll}
                      >
                        {documentTypes.map((type, index) => (
                          <div
                            key={index}
                            className={`px-4 py-1 md:py-2 flex justify-between items-center hover:bg-gray-100 cursor-pointer text-[6px] md:text-[18px]`}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDocumentTypeSelect(type);
                            }}
                          >
                            {type}
                            <div
                              className={`w-2 h-2 md:w-4 md:h-4 rounded-full ${
                                selectedDocumentType === type
                                  ? "bg-[#152C24]"
                                  : "bg-gray-200"
                              }`}
                            />
                          </div>
                        ))}
                      </div>
                      <div
                        className="custom-scroll-track"
                        onClick={handleTrackClick}
                      >
                        <div
                          ref={thumbRef}
                          className="custom-scroll-thumb"
                          style={{
                            height: `${thumbHeight}px`,
                            top: `${scrollTop}px`,
                            cursor: isDragging ? "grabbing" : "grab",
                          }}
                          onMouseDown={handleThumbMouseDown}
                        ></div>
                      </div>
                    </div>

                    <div className="w-full h-5 flex justify-end">
                      <img
                        src="https://res.cloudinary.com/dzngpgki4/image/upload/v1747461085/Expand_down_vbf4bb.png"
                        alt="scroll-down"
                        className="md:w-4 md:h-4 h-2 w-2 cursor-pointer"
                        onClick={() => {
                          contentRef.current.scrollBy({
                            top: 50,
                            behavior: "smooth",
                          });
                        }}
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
          
          <div className="flex justify-between items-center">
            <p className="font-normal italic text-[6px] md:text-[24px]">
              Please insert certificates
            </p>
            {/* Add this button */}
           
          </div>
        </div>

        <div className="grid grid-cols-4 md:gap-2 gap-1 mb-3 md:mb-6">
          {images.slice(0, 4).map((img, index) => (
            <div key={index} className="flex flex-col md:mt-6">
              <p className="text-[6px] md:text-[24px] font-light italic mb-2 md:mb-6 text-left md:min-h-[60px] md:w-[260px] leading-tight">
                {boxPlaceholders[index]}
              </p>
              <div className="relative bg-[#E7EBEB] rounded-[5px] h-[120px] md:h-[400px] w-full overflow-hidden">
  {img ? (
    <>
      <img
        src={img.previewUrl}
        alt="Certificate"
        className="object-cover w-full h-full"
      />
   
    </>
  ) : (
  <div>
  <label className="absolute top-1 right-1 md:top-2 md:right-2 cursor-pointer">
      <img
        src="https://res.cloudinary.com/dzngpgki4/image/upload/v1745668623/Add_square_duotone_gz7vxe.png"
        alt="Add Icon"
        className="w-4 md:w-[40px] h-4 md:h-[40px]"
      />
      <input
        type="file"
        accept="image/*,.pdf"
        onChange={(e) => handleImageChange(e, index)}
        className="hidden"
      />
    </label>
    <div className="absolute md:bottom-2 md:right-2 bottom-1 right-1">
        <img
          src="https://res.cloudinary.com/dzngpgki4/image/upload/v1746296659/Trash_ya7kiu.png"
          alt="Trash Icon"
          className="w-3 md:w-[24px] h-3 md:h-[24px] cursor-pointer"
          onClick={() => handleDeleteImage(index)}
        />
      </div>
  </div>
  )}
</div>
            </div>
          ))}
        </div>
      </div>

      {error && (
        <div className="mt-2 text-red-600 text-xs text-center">{error}</div>
      )}

      <SaveButtonWithModal onClick={handleSave} loading={saving} />

      <style jsx>{`
        .custom-scroll-area {
          position: relative;
          max-height: 300px;
          display: flex;
          overflow: hidden;
          width: 100%;
        }

        .custom-scroll-content {
          flex: 1;
          overflow-y: scroll;
          scrollbar-width: none;
          -ms-overflow-style: none;
          padding-right: 4px;
        }

        .custom-scroll-content::-webkit-scrollbar {
          display: none;
        }

        .custom-scroll-track {
          width: 8px;
          background: #e7ebeb;
          border-radius: 3px;
          position: relative;
          margin-left: 4px;
          cursor: pointer;
        }

        .custom-scroll-thumb {
          width: 100%;
          background: #979797;
          border-radius: 3px;
          position: absolute;
          right: 0;
          transition: background 0.2s;
        }

        .custom-scroll-thumb:hover {
          background: #444;
        }

        @media (max-width: 768px) {
          .custom-scroll-track {
            width: 4px;
            margin-left: 2px;
          }
        }
      `}</style>
    </div>
  );
};

export default OrganizationForm;